<script>
    import { browser } from '$app/environment';
    import { page } from '$app/stores';

    export let address;
    export let classes = '';
    export let zoom = '16';
    export let width = '622';
    export let height = '412';

    // dark mode style
    const darkStyle = [
        {
            featureType: 'all',
            elementType: 'labels.text.fill',
            stylers: [
                {
                    saturation: 36
                },
                {
                    color: '#000000'
                },
                {
                    lightness: 40
                }
            ]
        },
        {
            featureType: 'all',
            elementType: 'labels.text.stroke',
            stylers: [
                {
                    visibility: 'on'
                },
                {
                    color: '#000000'
                },
                {
                    lightness: 16
                }
            ]
        },
        {
            featureType: 'all',
            elementType: 'labels.icon',
            stylers: [
                {
                    visibility: 'off'
                }
            ]
        },
        {
            featureType: 'administrative',
            elementType: 'geometry.fill',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 20
                }
            ]
        },
        {
            featureType: 'administrative',
            elementType: 'geometry.stroke',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 17
                },
                {
                    weight: 1.2
                }
            ]
        },
        {
            featureType: 'administrative',
            elementType: 'labels',
            stylers: [
                {
                    visibility: 'off'
                }
            ]
        },
        {
            featureType: 'administrative.country',
            elementType: 'all',
            stylers: [
                {
                    visibility: 'simplified'
                }
            ]
        },
        {
            featureType: 'administrative.country',
            elementType: 'geometry',
            stylers: [
                {
                    visibility: 'simplified'
                }
            ]
        },
        {
            featureType: 'administrative.country',
            elementType: 'labels.text',
            stylers: [
                {
                    visibility: 'simplified'
                }
            ]
        },
        {
            featureType: 'administrative.province',
            elementType: 'all',
            stylers: [
                {
                    visibility: 'off'
                }
            ]
        },
        {
            featureType: 'administrative.locality',
            elementType: 'all',
            stylers: [
                {
                    visibility: 'simplified'
                },
                {
                    saturation: '-100'
                },
                {
                    lightness: '30'
                }
            ]
        },
        {
            featureType: 'administrative.neighborhood',
            elementType: 'all',
            stylers: [
                {
                    visibility: 'off'
                }
            ]
        },
        {
            featureType: 'administrative.land_parcel',
            elementType: 'all',
            stylers: [
                {
                    visibility: 'off'
                }
            ]
        },
        {
            featureType: 'landscape',
            elementType: 'all',
            stylers: [
                {
                    visibility: 'simplified'
                },
                {
                    gamma: '0.00'
                },
                {
                    lightness: '74'
                }
            ]
        },
        {
            featureType: 'landscape',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 20
                }
            ]
        },
        {
            featureType: 'landscape.man_made',
            elementType: 'all',
            stylers: [
                {
                    lightness: '3'
                }
            ]
        },
        {
            featureType: 'poi',
            elementType: 'all',
            stylers: [
                {
                    visibility: 'off'
                }
            ]
        },
        {
            featureType: 'poi',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 21
                }
            ]
        },
        {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [
                {
                    visibility: 'simplified'
                }
            ]
        },
        {
            featureType: 'road.highway',
            elementType: 'geometry.fill',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 17
                }
            ]
        },
        {
            featureType: 'road.highway',
            elementType: 'geometry.stroke',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 29
                },
                {
                    weight: 0.2
                }
            ]
        },
        {
            featureType: 'road.arterial',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 18
                }
            ]
        },
        {
            featureType: 'road.arterial',
            elementType: 'labels.text.fill',
            stylers: [
                {
                    color: '#dfdfdf'
                }
            ]
        },
        {
            featureType: 'road.local',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 16
                }
            ]
        },
        {
            featureType: 'road.local',
            elementType: 'labels.text.fill',
            stylers: [
                {
                    color: '#f3f3f3'
                }
            ]
        },
        {
            featureType: 'transit',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 19
                }
            ]
        },
        {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [
                {
                    color: '#000000'
                },
                {
                    lightness: 17
                }
            ]
        },
        {
            featureType: 'water',
            elementType: 'geometry.fill',
            stylers: [
                {
                    color: '#4e4c53'
                },
                {
                    saturation: '-20'
                }
            ]
        }
    ];

    // light style - use defualt for now
    const lightStyle = [];

    // Detect browsers default theme mode setting if user hasn't declared one
    const browserThemeMode = browser && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

    $: currentTheme = $page.data?.userPreferences?.siteTheme || browserThemeMode;
    $: mapStyle = currentTheme === 'dark' ? darkStyle : lightStyle;
    $: mapSrc = `/api/maps?zoom=${zoom}&size=${width}x${height}&center=${geoCode(address)}&markers=${geoCode(address)}&${convertStaticStyles(mapStyle)}`;

    // GEO Code address
    const geoCode = ({ address1, address2, city, province, country, zip }) => {
        const geocodeAddress = {
            address1,
            address2,
            city,
            province,
            country,
            zip
        };
        return encodeURI(Object.values(geocodeAddress).join(', '));
    };

    // Convert json styles to static_map format
    const convertStaticStyles = (styles) => {
        let result = [];
        styles.forEach((v) => {
            let style = '';
            if (v.stylers.length > 0) {
                // Needs to have a style rule to be valid.
                style += (v.hasOwnProperty('featureType') ? `feature:${v.featureType}` : 'feature:all') + '|';
                style += (v.hasOwnProperty('elementType') ? `element:${v.elementType}` : 'element:all') + '|';
                v.stylers.forEach((val) => {
                    const propertyname = Object.keys(val)[0];
                    const propertyval = val[propertyname].toString().replace('#', '0x');
                    style += `${propertyname}:${propertyval}|`;
                });
            }
            result.push(`style=${encodeURIComponent(style)}`);
        });

        return result.join('&');
    };
</script>

<svelte:head>
    <link rel="preload" as="image" href={mapSrc} />
</svelte:head>

<img src={mapSrc} alt="map" class={classes} {width} {height} fetchpriority="high" />
